import { ReactNode, useMemo } from 'react'
import { StructuralPlanningElementFormHeader } from '@structuralPlanningComponents'
import { TabPanel } from '@mui/lab'
import { Box, Stack, Tab } from '@mui/material'
import { HotkeyDeleteButton } from '@ui/actions'
import {
  AssemblyAssignmentForm,
  CollapsibleAssemblyBoxSection,
  CollapsibleDocumentationBoxSection,
  CollapsibleGeometryBoxSection,
  CollapsibleProblemsBoxSection,
  DocumentationItem,
} from '@editorComponents'
import GeometryForm from './components/GeometryForm'
import RoofFormBase from './components/RoofFormBase'
import { rectangularShapeTabs } from './tabs'

interface Props {
  element: VerticalRoofSlab | VerticalSlab
  label: string
  onCreate: (element: VerticalRoofSlab | VerticalSlab) => void
  onUpdate: (element: VerticalRoofSlab | VerticalSlab) => void
  onDelete: (elementGuid: string) => void
  onDeleteLocal: (elementGuid: string) => void
  isDeleting: boolean
  isSubmitting: boolean
  initialTab: 'geometry' | 'vertical-transmission'
  geometryEditable: boolean

  updateShape: (shape: { guid: string; shape: PolygonShape }) => void
  updateStorey: (guid: string, storey: string) => void

  storeys: string[]
  activeStorey?: string
  settingsSection?: ReactNode
  splitSection?: ReactNode
  onClose?: () => void
}

const RectangularShapeForm = ({
  element,
  onCreate,
  onUpdate,
  onDelete,
  onDeleteLocal,
  isDeleting,
  isSubmitting,
  initialTab,
  label,
  geometryEditable,

  updateShape,
  updateStorey,

  storeys,
  activeStorey,
  settingsSection,
  splitSection,
  onClose,
}: Props) => {
  const assemblyType = useMemo(() => {
    if (element.element_type === 'roof_slabs') return 'roof_slabs'
    if (element.element_type === 'vertical_roof_slabs') return 'roof_slabs'
    if (element.element_type === 'slabs') return 'slabs'
    if (element.element_type === 'vertical_slabs') return 'slabs'
  }, [element])

  if (element.is_local) {
    return (
      <Stack direction="column" spacing={1} p={1}>
        <GeometryForm
          roof={element}
          handleSubmit={slab => onCreate(slab)}
          handleDelete={() => onDeleteLocal(element.guid)}
          updateShape={updateShape}
          storeys={storeys}
          updateStorey={updateStorey}
          activeStorey={activeStorey}
          isSubmitting={isSubmitting}
          showCreateLabel
        />
      </Stack>
    )
  }

  return (
    <RoofFormBase
      initialTabValue={initialTab}
      selectedElement={element.guid}
      tab={
        <Tab
          value={rectangularShapeTabs.geometry.tab}
          label={rectangularShapeTabs.geometry.label}
          data-cy="tab-geometry"
        />
      }
    >
      <TabPanel value={rectangularShapeTabs.geometry.tab} sx={{ padding: 1 }}>
        <Stack direction="column" spacing={1}>
          <StructuralPlanningElementFormHeader
            elementGuid={element.guid}
            label={label}
            onClose={onClose}
          />
          <Stack direction="column" spacing={1}>
            <CollapsibleAssemblyBoxSection defaultExpanded={true}>
              <AssemblyAssignmentForm
                selectedElements={[element.guid]}
                elementType={assemblyType as ElementTypes}
                showSelectedElementsNumber={false}
                autoFocus={false}
              />
            </CollapsibleAssemblyBoxSection>

            {geometryEditable ? (
              <CollapsibleGeometryBoxSection defaultExpanded={true}>
                <GeometryForm
                  roof={element}
                  handleSubmit={onUpdate}
                  handleDelete={() => onDelete(element.guid)}
                  isDeleting={isDeleting}
                  updateShape={updateShape}
                  storeys={storeys}
                  updateStorey={updateStorey}
                  activeStorey={activeStorey}
                  isSubmitting={isSubmitting}
                />
              </CollapsibleGeometryBoxSection>
            ) : (
              <Box display="flex" width="100%" justifyContent="flex-end" component="div" px={1}>
                <HotkeyDeleteButton onClick={() => onDelete(element.guid)} loading={isDeleting}>
                  Löschen
                </HotkeyDeleteButton>
              </Box>
            )}

            {settingsSection}

            {splitSection}

            <CollapsibleDocumentationBoxSection defaultExpanded={false}>
              <DocumentationItem itemGuid={element.guid} />
            </CollapsibleDocumentationBoxSection>

            <CollapsibleProblemsBoxSection elementGuid={element.guid} defaultExpanded={true} />
          </Stack>
        </Stack>
      </TabPanel>
    </RoofFormBase>
  )
}

export default RectangularShapeForm
